import * as React from "react";
import { Box, Paper, PaperProps, useTheme, alpha, useMediaQuery, List, Typography, Divider, Chip } from "@mui/material";
import { DataGridProps, GridColDef } from "@mui/x-data-grid";
import DataGridCompat from "./DataGridCompat";
import ListItem from "./ListItem";

export interface GlassDataGridProps extends Omit<Partial<DataGridProps> & any, "sx"> {
  height?: number | string;
  sx?: (Partial<DataGridProps> & any)["sx"];
  paperSx?: PaperProps["sx"];
  renderMobileList?: (rows: any[]) => React.ReactNode;
}

const GlassDataGrid = React.forwardRef<HTMLDivElement, GlassDataGridProps>(
  ({ height = "70vh", sx, paperSx, renderMobileList, ...dataGridProps }, ref) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    // Helper function to get cell value - simplified version
    const getCellValue = React.useCallback((row: any, column: GridColDef) => {
      const field = column.field;
      if (!field) return "";

      // Try to get value from row directly first
      const rawValue = row[field];

      // If column has renderCell, we can't easily get the formatted value
      // So we'll just return the raw value or try to format it
      if (rawValue === null || rawValue === undefined) return "-";

      return rawValue;
    }, []);

    // Auto-generate mobile list view from columns and rows
    const renderAutoMobileList = React.useCallback((rows: any[], columns: GridColDef[]) => {
      if (!rows || rows.length === 0) {
        return (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No data available
            </Typography>
          </Box>
        );
      }

      // Get primary column (first visible column or first column)
      // Check hide property safely
      const primaryColumn = columns.find(col => {
        if (!col.field) return false;
        const colAny = col as any;
        return !colAny.hide;
      }) || columns[0];

      // Get secondary columns (limit to 2-3 most important)
      const secondaryColumns = columns
        .filter(col => {
          if (!col.field || col.field === primaryColumn?.field) return false;
          const colAny = col as any;
          return !colAny.hide;
        })
        .slice(0, 3);

      return (
        <List sx={{ py: 0 }}>
          {rows.map((row, index) => {
            const primaryValue = primaryColumn ? getCellValue(row, primaryColumn) : "";
            const primaryLabel = primaryColumn?.headerName || primaryColumn?.field || "";

            return (
              <React.Fragment key={row.id || index}>
                <ListItem
                  title={primaryLabel}
                  subtitle={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {String(primaryValue || "-")}
                      </Typography>
                      {secondaryColumns.map((col) => {
                        const value = getCellValue(row, col);
                        if (!value && value !== 0) return null;
                        return (
                          <Box key={col.field} sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                              {col.headerName || col.field}:
                            </Typography>{" "}
                            <Typography variant="caption" color="text.primary" component="span">
                              {String(value)}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  }
                />
                {index < rows.length - 1 && <Divider component="li" />}
              </React.Fragment>
            );
          })}
        </List>
      );
    }, [getCellValue]);

    // Conditional rendering: show mobile list if on mobile and has rows
    // if (isMobile && dataGridProps.rows && dataGridProps.columns) {
    //   const rows = dataGridProps.rows || [];
    //   const columns = dataGridProps.columns || [];

    //   return (
    //     <Paper
    //       ref={ref}
    //       sx={{
    //         height,
    //         position: "relative",
    //         overflow: "hidden",
    //         borderRadius: "12px",
    //         isolation: "isolate",
    //         background: isDark
    //           ? `linear-gradient(135deg, ${alpha("#0d1b2a", 0.92)} 0%, ${alpha("#0d1b2a", 0.85)} 40%, ${alpha("#12903C", 0.65)} 100%)`
    //           : `linear-gradient(135deg, rgba(255, 255, 255, 0.92) 0%, rgba(255, 255, 255, 0.8) 50%, rgba(18, 144, 60, 0.35) 100%)`,
    //         backdropFilter: "blur(24px) saturate(140%)",
    //         WebkitBackdropFilter: "blur(24px) saturate(140%)",
    //         border: `1.5px solid ${isDark ? alpha("#ffffff", 0.4) : alpha("#ffffff", 0.8)}`,
    //         boxShadow: isDark
    //           ? "0 8px 32px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
    //           : "0 8px 32px rgba(18, 144, 60, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)",
    //         "&::before": {
    //           content: '""',
    //           position: "absolute",
    //           top: 0,
    //           left: 0,
    //           right: 0,
    //           bottom: 0,
    //           borderRadius: "12px",
    //           pointerEvents: "none",
    //           boxShadow: isDark
    //             ? "inset 0 1px 0 0 rgba(255, 255, 255, 0.3)"
    //             : "inset 0 1px 0 0 rgba(255, 255, 255, 0.6)",
    //           zIndex: 1,
    //         },
    //         ...paperSx,
    //       }}
    //     >
    //       {/* Gradient overlays for glass shine effect */}
    //       <Box
    //         sx={{
    //           position: "absolute",
    //           top: 0,
    //           left: 0,
    //           right: 0,
    //           height: "50%",
    //           background: `linear-gradient(180deg, ${alpha("#ffffff", isDark ? 0.15 : 0.25)} 0%, transparent 100%)`,
    //           pointerEvents: "none",
    //           zIndex: 1,
    //           borderRadius: "12px 12px 0 0",
    //         }}
    //       />
    //       <Box
    //         sx={{
    //           position: "absolute",
    //           top: 0,
    //           left: 0,
    //           right: 0,
    //           bottom: 0,
    //           background: `linear-gradient(135deg, transparent 0%, ${alpha("#12903C", 0.03)} 100%)`,
    //           pointerEvents: "none",
    //           zIndex: 1,
    //           borderRadius: "12px",
    //         }}
    //       />
    //       <Box
    //         sx={{
    //           position: "relative",
    //           zIndex: 2,
    //           height: "100%",
    //           overflow: "auto",
    //           p: 2,
    //         }}
    //       >
    //         {renderMobileList
    //           ? renderMobileList(rows)
    //           : renderAutoMobileList(rows, columns)
    //         }
    //       </Box>
    //     </Paper>
    //   );
    // }

    return (
      <Paper
        ref={ref}
        sx={{
          height,
          position: "relative",
          overflow: "hidden",
          borderRadius: "12px",
          isolation: "isolate",
          background: isDark
            ? `linear-gradient(135deg, ${alpha("#0d1b2a", 0.92)} 0%, ${alpha("#0d1b2a", 0.85)} 40%, ${alpha("#12903C", 0.65)} 100%)`
            : `linear-gradient(135deg, rgba(255, 255, 255, 0.92) 0%, rgba(255, 255, 255, 0.8) 50%, rgba(18, 144, 60, 0.35) 100%)`,
          backdropFilter: "blur(24px) saturate(140%)",
          WebkitBackdropFilter: "blur(24px) saturate(140%)",
          border: `1.5px solid ${isDark ? alpha("#ffffff", 0.4) : alpha("#ffffff", 0.8)}`,
          boxShadow: isDark
            ? "0 8px 32px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
            : "0 8px 32px rgba(18, 144, 60, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: "12px",
            pointerEvents: "none",
            boxShadow: isDark
              ? "inset 0 1px 0 0 rgba(255, 255, 255, 0.3)"
              : "inset 0 1px 0 0 rgba(255, 255, 255, 0.6)",
            zIndex: 1,
          },
          ...paperSx,
        }}
      >
        {/* Gradient overlays for glass shine effect */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "50%",
            background: `linear-gradient(180deg, ${alpha("#ffffff", isDark ? 0.15 : 0.25)} 0%, transparent 100%)`,
            pointerEvents: "none",
            zIndex: 1,
            borderRadius: "12px 12px 0 0",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(135deg, transparent 0%, ${alpha("#12903C", 0.03)} 100%)`,
            pointerEvents: "none",
            zIndex: 1,
            borderRadius: "12px",
          }}
        />
        <DataGridCompat
          {...dataGridProps}
          sx={{
            border: "none",
            position: "relative",
            zIndex: 2,
            // Hide column headers when there's no data
            "& .MuiDataGrid-columnHeaders": {
              display: (!dataGridProps.rows || dataGridProps.rows.length === 0) ? "none" : "flex",
            },
            ...sx,
          }}
        />
      </Paper>
    );
  }
);

GlassDataGrid.displayName = "GlassDataGrid";

export default GlassDataGrid;

